import {DangerousGoods, DangerousGoodsInNewOrUpdateOrder} from "./dangeresGoods";
import {RelationType} from "./relation";
import {ContainerSizeType} from "./container";
import {CargoType} from "./cargo";
import {Shipper} from "./shipper";
import {PaymentMethod} from "./transport";
import {FerryModel} from "../../dictionaries/models/ferry";

export interface OrderModel {
    status: OrderStatusEnum;
    shipownerName: string;
    ferry: FerryModel;
    arrivalDepartureTime: Date | string | null;
    relationType: RelationType;
    trailerNo: string;
    regNo: string;
    id: string;
    isTemperatureRequired: boolean;
    isDangerous: boolean;
    isOversize: boolean;
    cargoType: CargoType | "";
}

export interface OrderDetailModel {
    id: string;

    // general information section
    status: OrderStatusEnum | "";
    relationType: RelationType | "";
    cargoType: CargoType | "";
    pinCode?: string;
    barcodeNo?: string;
    currentRoute?: string;
    currentRouteId?: string;
    gateNo?: string;
    createdAt: Date | string | null;

    // temperature section
    requestedTemp1?: number;
    requestedTemp2?: number;

    // transportation section
    trailerRegNo?: string;
    transportCompany?: string;
    transportCompanyId?: string;
    recipient?: string;
    lorryRegNo?: string;
    supplierLorryRegNo?: string;
    recipientLorryRegNo?: string;

    // voyage section
    shipownerName?: string;
    shipownerId?: string;
    voyageNo: string;
    arrivalDepartureTime: Date | null | string;
    ferryName?: string;
    ferryId?: string;
    originDestinationCountry?: string;
    originDestinationPort?: string;
    originDestinationPortId?: string;
    bookingNo?: string;

    // cargo section
    cargoDesc?: string;
    weight: number | null;
    length?: number;
    width?: number;
    height?: number;
    packingQuantity?: number;
    cbm?: number;
    packing?: string;
    packingId?: string;
    remarks?: string;
    wagonRegNo?: string;
    supplierWagonRegNo?: string;
    recipientWagonRegNo?: string;
    securingDeclaration: boolean;

    // container section
    containerNo?: string;
    containerSize?: ContainerSizeType | "";
    vgm?: number;

    // costs section
    storageTimeInMinutes: number;
    chargeableTimeInMinutes: number;
    paymentMethod: PaymentMethod | "";
    paymentDocumentNumber?: string;
    parkingCost?: string;

    // dangerous goods section
    dangerousGoods?: DangerousGoods[];
    isDangerousGoods: boolean;
}

export interface NewOrUpdateOrderModel {
    // general information section
    status: OrderStatusEnum | "";
    relationType: RelationType | "";
    cargoType: CargoType | "";
    pinCode?: string;
    barcodeNo?: string;

    // temperature section
    requestedTemp1?: number;
    requestedTemp2?: number;

    // transportation section
    trailerRegNo?: string;
    shipperId?: string;
    recipient?: string;
    lorryRegNo?: string;
    supplierLorryRegNo?: string;
    recipientLorryRegNo?: string;

    // voyage section
    voyageNo?: string;
    arrivalDepartureTime?: Date | null | string;
    ferryId?: string;
    shipownerId?: string;
    originDestinationPortId?: string;
    originDestinationCountry?: string;
    bookingNo?: string;

    // cargo section
    cargoDesc?: string;
    weight: number | null;
    length?: number;
    width?: number;
    height?: number;
    packing?: string;
    packingQuantity?: number;
    CBM?: number;
    remarks?: string;
    wagonRegNo?: string;
    supplierWagonRegNo?: string;
    recipientWagonRegNo?: string;
    securingDeclaration: boolean;

    // container section
    containerNo?: string;
    containerSize?: ContainerSizeType | string;
    VGM?: number;

    // costs section
    paymentMethod?: string;
    paymentDocumentNumber?: string;
    storageTime?: number;

    // dangerous goods section
    dangerousGoods?: DangerousGoodsInNewOrUpdateOrder[] | null;
}

export interface OrderCargoFileModel {
    relationType: RelationType;
    orderId: string;
    bookingNo: string;
    voyageNo?: string | null;
    trailerRegNo: string;
    lorryRegNo?: string;
    wagonRegNo?: string;
    supplierLorryRegNo?: string;
    recipientLorryRegNo?: string;
    supplierWagonRegNo?: string;
    recipientWagonRegNo?: string;
    containerNo: string;
    cargoType: CargoType;
    ferryName: string;
    arrivalDepartureTime: Date | null;
    weight: number;
    requestedTemp1: number;
    requestedTemp2: number;
    cbm: number;
    transportCompany: Shipper;
    recipient: string;
    timeOfDepositingLoad: Date | null;
    storageTime: number;
    paymentMethod: PaymentMethod;
    paymentDocumentNumber: string;
    remarks?: string;
    isDangerousGoods: boolean;
    status: OrderStatusEnum | "";
    timeToPickUp: Date | null;
    parkingCost?: string;
}

export interface TrailerOrContainerOrderFileModel {
    relationType: RelationType;
    orderId: string;
    bookingNo: string;
    voyageNo?: string | null;
    ferryName: string;
    arrivalDepartureTime: Date | null;
    cargoType: CargoType;
    trailerRegNo: string;
    lorryRegNo?: string;
    wagonRegNo?: string;
    supplierLorryRegNo?: string;
    recipientLorryRegNo?: string;
    supplierWagonRegNo?: string;
    recipientWagonRegNo?: string;
    containerNo: string;
    weight: number;
    requestedTemp1: number;
    requestedTemp2: number;
    transportCompany: Shipper;
    recipient: string;
    timeOfDepositingLoad: Date | null;
    timeToPickUp: Date | null;
    freeTimeFromFees: number;
    storageTime: number;
    paymentMethod: PaymentMethod;
    paymentDocumentNumber: string;
    pinCode: string;
    remarks?: string;
    isDangerousGoods: boolean;
    securingDeclaration: boolean;
    createdAt: Date | string | null;
    status: OrderStatusEnum | "";
}

export enum OrderStatusEnum {
    REGISTERED = "REGISTERED",
    CANCELED = "CANCELED",
    PARKING_ENTERED = "PARKING_ENTERED",
    PARKING_EXITED = "PARKING_EXITED",
    CONFIRMED_BY_MANIFEST = "CONFIRMED_BY_MANIFEST",
    CANCELED_BY_MANIFEST = "CANCELED_BY_MANIFEST",
    DEPO_ENTERED = "DEPO_ENTERED",
    DEPO_EXITED = "DEPO_EXITED",
    FERRY_ENTERED = "FERRY_ENTERED",
    FERRY_EXITED = "FERRY_EXITED"
}

export interface CurrentStatusModel {
    currentStatus: OrderStatusEnum;
    nextStatus: OrderStatusEnum;
    relationType: RelationType;
    primaryAction: DepoFlowActions | null;
    secondaryAction: DepoFlowActions | null;
}

export interface WagonsNumberToAssign {
    wagonRegNo: string;
    supplierWagonRegNo: string;
    recipientWagonRegNo: string;
}

export enum DepoFlowActions {
    ENTER_PORT = "ENTER_PORT",
    ENTER_DEPO = "ENTER_DEPO",
    EXIT_DEPO = "EXIT_DEPO",
    FILL_OUT_TCR = "FILL_OUT_TCR",
    ENTER_FERRY = "ENTER_FERRY",
    EXIT_FERRY = "EXIT_FERRY"
}

export interface AvailableSupervisorActions {
    availableActions: SupervisorFlowAction[];
}

export enum SupervisorFlowAction {
    GET_BACK_TO_REGISTRATION = "GET_BACK_TO_REGISTRATION",
    GET_BACK_TO_PORT_ENTRANCE = "GET_BACK_TO_PORT_ENTRANCE",
    GET_BACK_TO_PORT_EXIT = "GET_BACK_TO_PORT_EXIT",
    GET_BACK_TO_DEPO_ENTRANCE = "GET_BACK_TO_DEPO_ENTRANCE",
    GET_BACK_TO_DEPO_EXIT = "GET_BACK_TO_DEPO_EXIT",
    GET_BACK_TO_FERRY_ENTRANCE = "GET_BACK_TO_FERRY_ENTRANCE",
    GET_BACK_TO_FERRY_EXIT = "GET_BACK_TO_FERRY_EXIT" ,
    CANCEL= "CANCEL",
    ENTER_PORT = "ENTER_PORT",
    EXIT_PORT = "EXIT_PORT",
    ENTER_DEPO = "ENTER_DEPO",
    EXIT_DEPO = "EXIT_DEPO",
    ENTER_FERRY = "ENTER_FERRY",
    EXIT_FERRY = "EXIT_FERRY",
    CONFIRM_BY_MANIFEST = "CONFIRM_BY_MANIFEST",
    CANCEL_BY_MANIFEST = "CANCEL_BY_MANIFEST"
}

export interface ExecuteSupervisorFlowAction {
    supervisorFlowAction: SupervisorFlowAction;
}
